/**
 * Rastreio da chuva no mapa: o contorno de cada área com chuva ≥ 0,1 mm/h no
 * horário do GSMaP que casa com a imagem exibida (a mesma regra da camada da
 * chuva, `gsmap/tempo.ts`); a da ficha aberta, em violeta.
 *
 * Como o rastreio de nuvens: a imagem do satélite manda. Enquanto o arquivo
 * baixa, ou se ele não existe, o mapa fica só com a imagem — nunca com o
 * contorno de outro horário. Antes do primeiro play só o quadro na tela é
 * baixado; depois, o loop inteiro, e a reprodução espera cada contorno por no
 * máximo `PACIENCIA_MS`.
 *
 * Quase sem preenchimento, para não tingir a imagem: o suficiente para o
 * toque dentro da área abrir a ficha.
 */
import { suavizarFeicao } from '../map/suavizar';
import type { ReactNode } from 'react';
import type Feature from 'ol/Feature';
import type { FeatureLike } from 'ol/Feature';
import { getCenter } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import MultiPolygon from 'ol/geom/MultiPolygon';
import Polygon from 'ol/geom/Polygon';
import VectorLayer from 'ol/layer/Vector';
import type OlMap from 'ol/Map';
import VectorSource from 'ol/source/Vector';
import type Style from 'ol/style/Style';
import { quadroParaInstante } from '../gsmap/tempo';
import { isAbortError } from '../lib/fetchRetry';
import { subscribeSelector } from '../lib/store';
import { registerFrameGate } from '../loop/preloader';
import { registerFrameReadiness } from '../loop/render';
import { loopLoadAllowed, useLoopStore } from '../loop/store';
import { GOES_PROJ, registerGoesProjection } from '../map/projection';
import { Z } from '../map/zIndex';
import { useGoesStore } from '../state/goes';
import { shownTimeOf, useTimeStore } from '../state/time';
import { estiloDe } from '../tracking/layer';
import { TRACO_CHUVA, TRACO_CHUVA_SELECIONADO } from '../tracking/tracos';
import { carregarQuadro, RASTREIO_CHUVA_ATRIBUICAO, type QuadroChuva } from './api';
import { useRastreioChuvaStore, type EstadoNoQuadro, type HistoricoChuva, type SistemaChuva, type SistemaChuvaNaLista } from './store';

const PARALELOS = 4;
/** O quanto a reprodução espera um contorno: a imagem tem prioridade. */
export const PACIENCIA_MS = 3000;
/** Quadros guardados já projetados: as 48 h do GSMaP (96 horários). */
const MAX_QUADROS = 100;

type Entrada = { estado: 'carregando'; ctrl: AbortController; desde: number } | { estado: 'ok'; feicoes: Feature[] } | { estado: 'falhou' };

const formato = new GeoJSON();
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Valida a história: vetores do mesmo tamanho, `dt` numérico e crescente. Lixo vira null. */
export function lerHistorico(cru: unknown): HistoricoChuva | null {
  const h = cru as Partial<Record<keyof HistoricoChuva, unknown>> | null;
  if (!h || typeof h !== 'object' || !Array.isArray(h.dt) || h.dt.length === 0) return null;
  const n = h.dt.length;
  const dt = h.dt as unknown[];
  if (!dt.every((v, i) => typeof v === 'number' && Number.isFinite(v) && (i === 0 || v > (dt[i - 1] as number)))) return null;
  const serie = (v: unknown): (number | null)[] => (Array.isArray(v) && v.length === n ? v.map((x) => num(x)) : Array.from({ length: n }, () => null));
  return { dt: dt as number[], km2: serie(h.km2), pmax: serie(h.pmax), pmed: serie(h.pmed) };
}

/** GeoJSON (lon/lat) → features na projeção do mapa; cada contorno leva a ficha da área. */
export function lerQuadro(cru: unknown, q: Pick<QuadroChuva, 'ini' | 'fim' | 't'>): Feature[] {
  registerGoesProjection();
  const feicoes = formato.readFeatures(cru, { dataProjection: 'EPSG:4326', featureProjection: GOES_PROJ }) as Feature[];
  for (const f of feicoes) {
    if (f.get('k') !== 'c') continue;
    suavizarFeicao(f);
    const st = f.get('st') as unknown;
    f.set(
      'sistema',
      {
        uid: num(f.get('uid')),
        st: typeof st === 'string' && st ? st : null,
        vida: num(f.get('vida')),
        km2: num(f.get('km2')),
        pmax: num(f.get('pmax')),
        pmed: num(f.get('pmed')),
        dp: num(f.get('dp')),
        exp: num(f.get('exp')),
        vel: num(f.get('vel')),
        rumo: num(f.get('rumo')),
        hist: lerHistorico(f.get('hist')),
        ini: q.ini,
        fim: q.fim,
        t: q.t,
      } satisfies SistemaChuva,
      true,
    );
  }
  return feicoes;
}

/** As áreas de um quadro (sem as trajetórias), da maior para a menor. */
export function listarSistemas(feicoes: readonly Feature[]): SistemaChuvaNaLista[] {
  const lista: SistemaChuvaNaLista[] = [];
  for (const f of feicoes) {
    const sistema = f.get('sistema') as SistemaChuva | undefined;
    const g = f.getGeometry();
    if (!sistema || !g) continue;
    // O maior pedaço de um MultiPolygon: o ponto cai dentro da chuva, não num vão.
    let dentro: number[];
    if (g instanceof Polygon) dentro = g.getInteriorPoint().getCoordinates();
    else if (g instanceof MultiPolygon) {
      const partes = g.getPolygons().sort((a, b) => b.getArea() - a.getArea());
      dentro = partes[0]?.getInteriorPoint().getCoordinates() ?? getCenter(g.getExtent());
    } else dentro = getCenter(g.getExtent());
    lista.push({ sistema, ponto: [dentro[0]!, dentro[1]!], extensao: g.getExtent() as [number, number, number, number] });
  }
  return lista.sort((a, b) => (b.sistema.km2 ?? 0) - (a.sistema.km2 ?? 0));
}

const ESTILO = estiloDe(TRACO_CHUVA);
const ESTILO_SELECIONADO = estiloDe(TRACO_CHUVA_SELECIONADO);
/**
 * Só os contornos. A trajetória vem no arquivo, mas não é desenhada: numa área
 * de chuva de 0,1 mm/h o centro pula a cada fusão e divisão (medido em
 * 03/11/2026: zigue-zagues de centenas de km), e a linha enganaria. O rumo e a
 * velocidade estão na ficha.
 */
function estilo(f: FeatureLike): Style[] | undefined {
  if (f.get('k') === 't') return undefined;
  const sel = useRastreioChuvaStore.getState().selecao;
  return sel !== null && f.get('uid') === sel ? ESTILO_SELECIONADO : ESTILO;
}

/** O gerenciador ativo, para a ficha achar o contorno na tela. */
let ativo: RastreioChuvaLayer | null = null;

/** A extensão (projeção do mapa) da área `uid` na tela agora, ou null. */
export function extensaoNaTela(uid: number): number[] | null {
  return ativo?.extensaoDe(uid) ?? null;
}

const sateliteLigado = () => useGoesStore.getState().layers.some((l) => l.visible);
/** Subcamada do Satélite, como o rastreio de nuvens: desligado ele, some junto. */
const emUso = () => useRastreioChuvaStore.getState().ligado && sateliteLigado();

export class RastreioChuvaLayer {
  readonly layer: VectorLayer<VectorSource>;
  private readonly source: VectorSource;
  private readonly cache = new Map<string, Entrada>();
  private fila: QuadroChuva[] = [];
  private emVoo = 0;
  private naTela: string | null = null;
  private quadros: QuadroChuva[] = [];
  private meios: number[] = [];
  private readonly esperas = new Map<string, (() => void)[]>();
  private readonly paradas: (() => void)[] = [];

  constructor(
    private readonly map: OlMap,
    renderPopup: (s: SistemaChuva) => ReactNode | null,
  ) {
    this.source = new VectorSource({ attributions: RASTREIO_CHUVA_ATRIBUICAO });
    this.layer = new VectorLayer({
      source: this.source,
      style: estilo,
      zIndex: Z.TRACKING_CHUVA,
      className: 'ol-layer amaview-rastreio-chuva',
      properties: { id: 'rastreio-chuva', title: 'Rastreio da chuva' },
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    });
    this.layer.set('popupMovel', true);
    this.layer.set('popupEvitar', (feature: FeatureLike) => {
      const s = feature.get('sistema') as SistemaChuva | undefined;
      return s?.uid != null ? areaDaChuva(s.uid) : null;
    });
    this.layer.set('popup', (feature: FeatureLike): ReactNode | null => {
      const s = feature.get('sistema') as SistemaChuva | undefined;
      return s ? renderPopup(s) : null;
    });
    map.addLayer(this.layer);
    ativo = this;

    this.paradas.push(
      useRastreioChuvaStore.subscribe((a, b) => {
        if (a.selecao !== b.selecao) this.layer.changed();
        if (a.ligado !== b.ligado || a.indice !== b.indice) this.replanejar();
      }),
      subscribeSelector(useGoesStore, (s) => s.layers.some((l) => l.visible), () => this.replanejar()),
      useTimeStore.subscribe((a, b) => {
        if (a.frames !== b.frames || (a.index !== b.index && !loopLoadAllowed())) this.replanejar();
        else if (shownTimeOf(a) !== shownTimeOf(b)) this.mostrar();
      }),
      subscribeSelector(useLoopStore, (s) => loopLoadAllowed(s), () => this.replanejar()),
      registerFrameGate((i) => this.pronto(useTimeStore.getState().frames[i] ?? null)),
      registerFrameReadiness(() => this.quandoPronto(shownTimeOf(useTimeStore.getState()))),
    );
    this.replanejar();
  }

  private quadroDe(t: number | null): QuadroChuva | null {
    return quadroParaInstante(this.quadros, this.meios, t);
  }

  /** O contorno do instante `t` já pode ser desenhado, não existe, ou já se esperou demais por ele? */
  pronto(t: number | null, agora: number = Date.now()): boolean {
    if (!emUso()) return true;
    const q = this.quadroDe(t);
    const e = q ? this.cache.get(q.c) : undefined;
    if (!e) return true;
    return e.estado !== 'carregando' || agora - e.desde > PACIENCIA_MS;
  }

  /** Para a exportação: espera o arquivo do quadro NA TELA chegar (ou falhar). */
  quandoPronto(t: number | null): Promise<void> | void {
    if (!emUso()) return;
    const q = this.quadroDe(t);
    if (!q) return;
    const e = this.cache.get(q.c);
    if (e && e.estado !== 'carregando') return;
    if (!e) this.pedir(q);
    return new Promise((resolve) => {
      const lista = this.esperas.get(q.c) ?? [];
      lista.push(resolve);
      this.esperas.set(q.c, lista);
    });
  }

  /** Quadros necessários para o loop atual; baixa o que falta e descarta o que saiu. */
  replanejar(): void {
    const ligado = emUso();
    this.layer.setVisible(ligado);
    this.quadros = [...(useRastreioChuvaStore.getState().indice?.quadros ?? [])];
    this.meios = this.quadros.map((q) => q.t);
    if (!ligado) {
      this.fila = [];
      for (const [c, e] of this.cache) {
        if (e.estado === 'carregando') {
          e.ctrl.abort();
          this.cache.delete(c);
        }
      }
      this.mostrar();
      return;
    }
    const { frames, index } = useTimeStore.getState();
    const precisa = new Map<string, QuadroChuva>();
    // Do quadro atual para trás: o que está na tela chega primeiro. Vários quadros do satélite
    // casam com o mesmo horário do GSMaP (30 min): cada arquivo entra uma vez.
    const todoLoop = loopLoadAllowed();
    for (let k = 0; k < frames.length && precisa.size < MAX_QUADROS; k++) {
      const i = (index - k + frames.length) % frames.length;
      const q = this.quadroDe(frames[i] ?? null);
      if (!q) continue;
      if (todoLoop || i === index || this.cache.get(q.c)?.estado === 'ok') precisa.set(q.c, q);
    }
    for (const [c, e] of this.cache) {
      if (precisa.has(c)) continue;
      if (e.estado === 'carregando') e.ctrl.abort();
      this.cache.delete(c);
    }
    this.fila = [...precisa.values()].filter((q) => !this.cache.has(q.c));
    this.bombear();
    this.mostrar();
  }

  private pedir(q: QuadroChuva): void {
    if (this.cache.has(q.c)) return;
    this.fila = [q, ...this.fila.filter((x) => x.c !== q.c)];
    this.bombear();
  }

  private bombear(): void {
    while (this.emVoo < PARALELOS && this.fila.length) {
      const q = this.fila.shift()!;
      if (this.cache.has(q.c)) continue;
      const ctrl = new AbortController();
      this.cache.set(q.c, { estado: 'carregando', ctrl, desde: Date.now() });
      this.emVoo++;
      carregarQuadro(q.c, ctrl.signal)
        .then((cru) => {
          if (this.cache.get(q.c)?.estado !== 'carregando') return;
          this.cache.set(q.c, { estado: 'ok', feicoes: lerQuadro(cru, q) });
        })
        .catch((e: unknown) => {
          if (isAbortError(e) || this.cache.get(q.c)?.estado !== 'carregando') return;
          console.warn(`[AmaView] Rastreio da chuva ${q.c} não carregou:`, e);
          this.cache.set(q.c, { estado: 'falhou' });
        })
        .finally(() => {
          this.emVoo--;
          this.mostrar();
          const esperas = this.esperas.get(q.c);
          if (esperas && this.cache.get(q.c)?.estado !== 'carregando') {
            this.esperas.delete(q.c);
            esperas.forEach((r) => r());
          }
          this.bombear();
        });
    }
  }

  /** Desenha o quadro do instante exibido — ou nada, se ele não está pronto. */
  mostrar(): void {
    const q = emUso() ? this.quadroDe(shownTimeOf(useTimeStore.getState())) : null;
    let estado: EstadoNoQuadro;
    if (!q) {
      this.trocar(null, []);
      estado = 'sem';
    } else {
      const e = this.cache.get(q.c);
      if (e?.estado === 'ok') {
        this.trocar(q.c, e.feicoes);
        estado = 'ok';
      } else {
        this.trocar(null, []);
        estado = e?.estado === 'falhou' ? 'falhou' : 'carregando';
        if (!e) this.pedir(q);
      }
    }
    if (useRastreioChuvaStore.getState().noQuadro !== estado) useRastreioChuvaStore.setState({ noQuadro: estado });
  }

  private trocar(c: string | null, feicoes: Feature[]): void {
    if (c === this.naTela) return;
    this.naTela = c;
    this.source.clear(true);
    if (feicoes.length) this.source.addFeatures(feicoes);
    useRastreioChuvaStore.setState({ sistemas: listarSistemas(feicoes) });
  }

  /** Recoloca na fila os quadros que falharam (ex.: a conexão caiu por um minuto). */
  tentarDeNovo(): void {
    let algum = false;
    for (const [c, e] of this.cache) {
      if (e.estado === 'falhou') {
        this.cache.delete(c);
        algum = true;
      }
    }
    if (algum) this.replanejar();
  }

  /** O sistema `uid` no quadro do instante `t`, se o arquivo dele já está na memória. */
  sistemaEm(uid: number, t: number | null): SistemaChuva | null {
    const q = this.quadroDe(t);
    const e = q ? this.cache.get(q.c) : undefined;
    if (e?.estado !== 'ok') return null;
    for (const f of e.feicoes) {
      const s = f.get('sistema') as SistemaChuva | undefined;
      if (s?.uid === uid) return s;
    }
    return null;
  }

  extensaoDe(uid: number): number[] | null {
    for (const f of this.source.getFeatures()) {
      const s = f.get('sistema') as SistemaChuva | undefined;
      if (s?.uid === uid) return f.getGeometry()?.getExtent() ?? null;
    }
    return null;
  }

  dispose(): void {
    if (ativo === this) ativo = null;
    this.paradas.forEach((p) => p());
    for (const e of this.cache.values()) if (e.estado === 'carregando') e.ctrl.abort();
    this.cache.clear();
    this.esperas.forEach((l) => l.forEach((r) => r()));
    this.esperas.clear();
    this.source.clear(true);
    this.map.removeLayer(this.layer);
  }
}

/** A área que a ficha não deve cobrir: o contorno da área como está na tela AGORA. */
export function areaDaChuva(uid: number): () => number[] | null {
  return () => extensaoNaTela(uid);
}

/** A mesma área no horário exibido (a ficha acompanha o player), ou null se ela não está nele. */
export function chuvaNoInstante(uid: number, t: number | null): SistemaChuva | null {
  return ativo?.sistemaEm(uid, t) ?? null;
}
